import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const JobInformationPanel = ({ job, onEditJob, onCloseJob }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' })?.format(amount);
  };

  return (
    <div 
      className="bg-white border-2 border-slate-200 rounded-lg overflow-hidden"
      style={{
        boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
      }}
    >
      <div className="relative h-48 md:h-56 lg:h-64 overflow-hidden">
        <Image
          src={job?.image}
          alt={job?.imageAlt}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-4 right-4 flex gap-2">
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
            job?.status === 'open' ? 'bg-success text-success-foreground' :
            job?.status === 'in_progress' ? 'bg-warning text-warning-foreground' :
            'bg-muted text-muted-foreground'
          }`}>
            {job?.status === 'open' ? 'Open' : job?.status === 'in_progress' ? 'In Uitvoering' : 'Voltooid'}
          </div>
        </div>
      </div>
      <div className="p-4 md:p-6 lg:p-8">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
              {job?.title}
            </h1>
            <div className="flex items-center gap-2 text-muted-foreground caption">
              <Icon name="Calendar" size={16} />
              <span>Geplaatst op {formatDate(job?.postedDate)}</span>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" iconName="Edit" onClick={onEditJob}>
              Bewerken
            </Button>
            {job?.status === 'open' && (
              <Button variant="destructive" size="sm" iconName="XCircle" onClick={onCloseJob}>
                Sluiten
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="MapPin" size={18} className="text-primary" />
              <span className="caption text-muted-foreground">Locatie</span>
            </div>
            <p className="font-medium text-foreground">{job?.location}</p>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Wrench" size={18} className="text-primary" />
              <span className="caption text-muted-foreground">Type Werk</span>
            </div>
            <p className="font-medium text-foreground">{job?.workType}</p>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Clock" size={18} className="text-primary" />
              <span className="caption text-muted-foreground">Duur</span>
            </div>
            <p className="font-medium text-foreground">{job?.duration}</p>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Euro" size={18} className="text-primary" />
              <span className="caption text-muted-foreground">Budget</span>
            </div>
            <p className="font-medium text-foreground">{formatCurrency(job?.budget)}</p>
          </div>
        </div>

        <div className="mb-6">
          <h2 className="text-lg md:text-xl font-semibold text-foreground mb-3">Beschrijving</h2>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed whitespace-pre-line">
            {job?.description}
          </p>
        </div>

        <div className="mb-6">
          <h2 className="text-lg md:text-xl font-semibold text-foreground mb-3">Vereiste Vaardigheden</h2>
          <div className="flex flex-wrap gap-2">
            {job?.requiredSkills?.map((skill, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <h2 className="text-lg md:text-xl font-semibold text-foreground mb-3">Tijdlijn</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="CalendarCheck" size={20} color="var(--color-success)" />
              </div>
              <div>
                <p className="caption text-muted-foreground">Startdatum</p>
                <p className="font-medium text-foreground">{formatDate(job?.startDate)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="CalendarClock" size={20} color="var(--color-warning)" />
              </div>
              <div>
                <p className="caption text-muted-foreground">Einddatum (verwacht)</p>
                <p className="font-medium text-foreground">{formatDate(job?.expectedEndDate)}</p>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-lg md:text-xl font-semibold text-foreground mb-3">Locatie op Kaart</h2>
          <div className="w-full h-64 md:h-80 lg:h-96 rounded-lg overflow-hidden border border-border">
            <iframe
              width="100%"
              height="100%"
              loading="lazy"
              title={job?.location}
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://www.google.com/maps?q=${job?.coordinates?.lat},${job?.coordinates?.lng}&z=14&output=embed`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobInformationPanel;